import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';

import {
  is403Error,
  getApiClient,
  overrideOrganizationHeader,
} from '@cast/api-client';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import {
  ListRoleBindingsResponse,
  RoleBindingStateType,
} from '@cast/types-organization-management';
import { makeIsEnabled } from '@cast/utils';

import { roleBindingsRetryOptions } from './roleBindingsRetry';

export const RBAC_ENTERPRISE_ROLE_BINDINGS_QUERY_KEY =
  'rbac-enterprise-role-bindings';

export const getRbacEnterpriseRoleBindingsInfiniteQueryKey = (
  params: {
    enterpriseId?: string;
    organizationId?: string[];
    subjectId?: string[];
  } = {}
) =>
  [
    RBAC_ENTERPRISE_ROLE_BINDINGS_QUERY_KEY,
    params.enterpriseId,
    params.organizationId,
    params.subjectId,
  ] as const;

const getRbacEnterpriseRoleBindingsInfiniteQueryOptions = ({
  enterpriseId,
  organizationId,
  subjectId,
  enabled = true,
  shouldTriggerRetry = false,
}: {
  enterpriseId: string;
  organizationId?: string[];
  subjectId?: string[];
  enabled?: boolean;
  shouldTriggerRetry?: boolean;
}) =>
  infiniteQueryOptions({
    queryKey: getRbacEnterpriseRoleBindingsInfiniteQueryKey({
      enterpriseId,
      organizationId,
      subjectId,
    }),
    initialPageParam: '0',
    getNextPageParam: (res: ListRoleBindingsResponse) => {
      return res.nextPageCursor === '0' || res.nextPageCursor == res.totalCount
        ? undefined
        : res.nextPageCursor;
    },
    getPreviousPageParam: (res: ListRoleBindingsResponse) => {
      return res.prevPageCursor === '0' || !res.prevPageCursor
        ? undefined
        : res.prevPageCursor;
    },
    queryFn: async ({ pageParam }) => {
      try {
        const res = await getApiClient().enterprises.listRoleBindings(
          {
            enterpriseId,
            pageLimit: '100',
            pageCursor: pageParam,
            organizationId: organizationId,
            subjectId: subjectId,
          },
          {
            headers: overrideOrganizationHeader(enterpriseId || undefined),
          }
        );

        return res.data;
      } catch (error) {
        if (is403Error(error) && !shouldTriggerRetry) {
          // 403 means no enterprise access - return empty response instead of throwing
          return {
            items: [],
            totalCount: 0,
            nextPageCursor: '0',
          };
        }
        throw error; // Re-throw other errors (500, network issues, etc.)
      }
    },
    select: (data) => data.pages.flatMap((page) => page.items ?? []),
    ...(shouldTriggerRetry && roleBindingsRetryOptions),
    refetchInterval: ({ state }) => {
      const isProcessing = state.data?.pages
        .flatMap((page) => page.items ?? [])
        .some(
          (binding) =>
            binding?.status?.[0]?.state === RoleBindingStateType.ACCEPTED
        );

      return isProcessing ? 5_000 : false;
    },
    enabled: makeIsEnabled(!!enterpriseId, enabled),
  });

export type RbacEnterpriseRoleBindingsInfiniteQueryArgs = {
  enabled?: boolean;
  enterpriseId?: string;
  organizationId?: string[];
  subjectId?: string[];
  shouldTriggerRetry?: boolean;
};

export const useRbacEnterpriseRoleBindingsInfiniteQuery = ({
  enabled = true,
  enterpriseId,
  organizationId,
  subjectId,
  shouldTriggerRetry,
}: RbacEnterpriseRoleBindingsInfiniteQueryArgs = {}) => {
  const { currentOrganization } = useOrganizations();

  return useInfiniteQuery(
    getRbacEnterpriseRoleBindingsInfiniteQueryOptions({
      enterpriseId: enterpriseId ?? currentOrganization?.parentId ?? '',
      organizationId,
      subjectId,
      enabled,
      shouldTriggerRetry,
    })
  );
};
